import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateTenant, logoutFetch } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import ownerAPI from "../../../services/ownerApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { isClubOwner, normalizeRole, hasAdminAccess } from "../../../utils/roles";
import AdminPatientImport from "../AdminPatients/AdminPatientImport";
import SettingsPortalBrandingPanel from "./SettingsPortalBrandingPanel";
import SettingsAcquiringPanel from "./SettingsAcquiringPanel";
import AttendancePollSettingsPanel from "./AttendancePollSettingsPanel";
import AbsenceChargeSettingsPanel from "./AbsenceChargeSettingsPanel";
import TrainerNotifySettingsPanel from "./TrainerNotifySettingsPanel";
import { AdminPushBlock } from "../AdminPortal/staffProfileShared";
import {
  Button,
  Field,
  Input,
  PhoneInput,
  Select,
  SkeletonCard,
  Tabs,
  Textarea,
  useToast,
  Avatar,
} from "../../ui";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { HubFormCard, HubScreen, HubSegment } from "../AdminPortal/HubPwa";
import { resolvePlanAccess } from "../../../utils/planEntitlements";
import { isClubAcquiringEmailAllowed } from "../../../utils/clubAcquiringAccess";
import { forceOnboarding } from "../AdminOnboarding/onboardingStorage";
import { useTrainingMode } from "../../../training";
import { ActTarget, ActivationPageGuide, useActivation } from "../../../activation";
import { isOnboardingEnabled, isQuickStartEnabled, isHelpEnabled } from "../../../utils/productFlags";
import { downloadBlobFile } from "../../../utils/excelCsv";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";

const DEFAULT = {
  name: "",
  address: "",
  phone: "",
  logo_url: "",
  subdomain: "",
  status: "",
  timezone: "Europe/Moscow",
  slot_step_minutes: 15,
  slot_buffer_minutes: 0,
  min_booking_lead_hours: 2,
  cancellation_lead_hours: 24,
  patient_reminder_enabled: true,
  trainers_see_all_schedule: false,
  auto_approve_registrations: true,
  portal_accent_color: null,
  portal_cover_url: null,
  portal_font_family: null,
  portal_theme_mode: "light",
  portal_description: "",
  portal_gallery: [],
  website_enabled: false,
  website_template: "classic",
  website_content: {
    headline: "",
    subheadline: "",
    cta_label: "Записаться на пробное",
    about_title: "О клубе",
    about_text: "",
    show_gallery: true,
    show_trial_form: true,
    contacts_note: "",
  },
  website_templates: [{ id: "classic", label: "Классика" }],
};

const STATUS_LABELS = {
  trial: "Пробный",
  active: "Активна",
  suspended: "Приостановлена",
};

/** Часовые пояса клуба — время в расписании групп считается по этому поясу. */
const CLUB_TIMEZONES = [
  { value: "Europe/Kaliningrad", label: "Калининград (UTC+2)" },
  { value: "Europe/Moscow", label: "Москва (UTC+3)" },
  { value: "Europe/Samara", label: "Самара (UTC+4)" },
  { value: "Asia/Yekaterinburg", label: "Екатеринбург (UTC+5)" },
  { value: "Asia/Omsk", label: "Омск (UTC+6)" },
  { value: "Asia/Novosibirsk", label: "Новосибирск (UTC+7)" },
  { value: "Asia/Krasnoyarsk", label: "Красноярск (UTC+7)" },
  { value: "Asia/Irkutsk", label: "Иркутск (UTC+8)" },
  { value: "Asia/Yakutsk", label: "Якутск (UTC+9)" },
  { value: "Asia/Vladivostok", label: "Владивосток (UTC+10)" },
  { value: "Asia/Magadan", label: "Магадан (UTC+11)" },
  { value: "Asia/Kamchatka", label: "Камчатка (UTC+12)" },
];

const BASE_TABS = [
  { id: "general", label: "Общие" },
  { id: "portal", label: "Персонализация" },
  { id: "notify", label: "Уведомления" },
  { id: "data", label: "Данные" },
];

const REMOVED_TABS = new Set([
  "team",
  "booking",
  "venues",
  "directions",
  "structure",
  "registration",
  "website",
]);

export default function AdminSettings() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isApp = useAdminHubUi();
  const {
    active: trainingActive,
    currentLesson,
    trainingSubstepId,
    exitTraining,
  } = useTrainingMode();
  const { visible: activationVisible, reopen, dismiss } = useActivation();
  const { tenant: authTenant, entitlements, billing, profile, role, club } = useSelector(
    (state) => state.auth,
  );
  const owner = isClubOwner({ role, profileRole: profile?.role, club });
  const onboardingEnabled = isOnboardingEnabled();
  const quickStartEnabled = isQuickStartEnabled(authTenant);
  const helpEnabled = isHelpEnabled();
  const plan = useMemo(
    () => resolvePlanAccess(entitlements, authTenant, billing),
    [entitlements, authTenant, billing],
  );
  const showPaymentsTab =
    plan.onlineAcquiring && isClubAcquiringEmailAllowed(profile?.email);
  const visibleTabs = useMemo(() => {
    const tabs = BASE_TABS.filter((t) => {
      if (t.id === "portal") return plan.showPortalBrandingTab;
      return true;
    });
    if (showPaymentsTab) {
      const dataIdx = tabs.findIndex((t) => t.id === "data");
      const paymentTab = { id: "payments", label: "Оплата" };
      if (dataIdx >= 0) tabs.splice(dataIdx, 0, paymentTab);
      else tabs.push(paymentTab);
    }
    return tabs;
  }, [plan.showPortalBrandingTab, showPaymentsTab]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const legacyTab = REMOVED_TABS.has(tabFromUrl) ? "general" : tabFromUrl;
  const validTab = visibleTabs.some((t) => t.id === legacyTab) ? legacyTab : "general";
  const [tab, setTab] = useState(validTab);

  useEffect(() => {
    if (validTab !== tab) setTab(validTab);
    if (tabFromUrl && REMOVED_TABS.has(tabFromUrl)) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [validTab, tab, tabFromUrl, searchParams, setSearchParams]);

  const setTabAndUrl = useCallback(
    (id) => {
      setTab(id);
      const next = new URLSearchParams(searchParams);
      if (id === "general") next.delete("tab");
      else next.set("tab", id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!trainingActive || currentLesson?.id !== "settings" || !trainingSubstepId) {
      return;
    }
    const tabBySub = {
      settings_club: "general",
      settings_branding_logo: "portal",
      settings_branding_cover: "portal",
      settings_branding_accent: "portal",
      settings_branding_theme: "portal",
      settings_notify: "notify",
    };
    const nextTab = tabBySub[trainingSubstepId];
    if (nextTab && nextTab !== tab) setTabAndUrl(nextTab);
  }, [trainingActive, currentLesson?.id, trainingSubstepId, tab, setTabAndUrl]);

  useEffect(() => {
    if (!trainingActive || currentLesson?.id !== "settings" || !trainingSubstepId) {
      return;
    }
    const t = window.setTimeout(() => {
      const sel = `[data-act-sub="${trainingSubstepId}"]`;
      (
        document.querySelector(`${sel}[data-act-active="1"]`) ||
        document.querySelector(sel)
      )?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 280);
    return () => window.clearTimeout(t);
  }, [trainingActive, currentLesson?.id, trainingSubstepId, tab]);

  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deletingClub, setDeletingClub] = useState(false);
  const [showDeleteClub, setShowDeleteClub] = useState(false);

  const restoreOnboarding = () => {
    if (!authTenant?.id) return;
    forceOnboarding(authTenant.id);
    navigate("/manage/app");
    toast.success("Мастер настройки снова на главном экране");
  };

  const restartQuickSetup = () => {
    if (trainingActive) exitTraining();
    reopen();
    navigate("/manage/app");
    toast.success("Быстрый старт — 6 шагов с подсказками");
  };

  const exitAnyGuide = () => {
    if (trainingActive) exitTraining();
    else if (activationVisible) dismiss();
  };

  const deleteCurrentClub = async () => {
    if (!owner || !authTenant?.id) return;
    const expected = String(form.name || authTenant.name || "").trim();
    if (!expected || deleteConfirmName.trim() !== expected) {
      toast.error("Введите название клуба точно как в настройках");
      return;
    }
    if (
      !window.confirm(
        `Удалить клуб «${expected}»?\n\nДоступ сотрудников и клиентов будет закрыт. Данные сохранятся для статистики, восстановить клуб нельзя.`,
      )
    ) {
      return;
    }
    setDeletingClub(true);
    try {
      await ownerAPI.deleteClub(authTenant.id, expected);
      toast.success("Клуб удалён");
      await dispatch(logoutFetch());
      navigate("/account", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось удалить клуб");
    } finally {
      setDeletingClub(false);
    }
  };

  useEffect(() => {
    clinicAPI
      .getTenantSettings()
      .then((r) => {
        if (r.tenant) setForm({ ...DEFAULT, ...r.tenant });
      })
      .catch((e) => toast.error(e.response?.data?.error || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [toast]);

  const buildPayload = () => {
    const payload = { ...form };
    delete payload.subdomain;
    delete payload.status;
    delete payload.id;
    delete payload.members_count;
    delete payload.createdAt;
    delete payload.website_enabled;
    delete payload.website_template;
    delete payload.website_content;
    delete payload.website_templates;
    return payload;
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await clinicAPI.updateTenantSettings(buildPayload());
      setForm({ ...DEFAULT, ...r.tenant });
      dispatch(
        updateTenant({
          trainers_see_all_schedule: !!r.tenant?.trainers_see_all_schedule,
          timezone: r.tenant?.timezone || "Europe/Moscow",
        }),
      );
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const mergeTenant = (tenant) => {
    if (!tenant) return;
    setForm((f) => ({ ...f, ...tenant }));
    dispatch(
      updateTenant({
        portal_accent_color: tenant.portal_accent_color ?? null,
        portal_cover_url: tenant.portal_cover_url ?? null,
        portal_font_family: tenant.portal_font_family ?? null,
        portal_theme_mode: tenant.portal_theme_mode === "dark" ? "dark" : "light",
        portal_description: tenant.portal_description ?? null,
        portal_gallery: tenant.portal_gallery ?? [],
        website_enabled: !!tenant.website_enabled,
        website_template: tenant.website_template || "classic",
        website_content: tenant.website_content,
        name: tenant.name,
        logo_url: tenant.logo_url,
        address: tenant.address,
        phone: tenant.phone,
        trainers_see_all_schedule: !!tenant.trainers_see_all_schedule,
      }),
    );
  };

  const saveButton = (fullWidth = false) => (
    <div className={isApp ? aa.settingsHubSaveRow : undefined}>
      <Button
        onClick={save}
        disabled={saving}
        className={fullWidth ? aa.hubPrimaryCta : undefined}
        style={fullWidth ? { width: "100%" } : undefined}
      >
        {saving ? "Сохранение…" : "Сохранить"}
      </Button>
    </div>
  );

  const generalFieldsDesktop = (
    <div className={md.desktopSettingsGeneralCard}>
      <div className={md.desktopSettingsGeneralHead}>
        <h2 className={md.desktopSettingsGeneralTitle}>Профиль клуба</h2>
        <p className={md.desktopSettingsGeneralHint}>
          Название, контакты и короткое описание — это видят клиенты в приложении
        </p>
      </div>
      <div className={md.desktopSettingsFormStack}>
        <div className={md.desktopSettingsFormGrid}>
          <Field label="Название клуба">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="Телефон">
            <PhoneInput
              value={form.phone || ""}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </Field>
        </div>
        <Field label="Адрес">
          <Input
            value={form.address || ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Город, улица, корпус"
          />
        </Field>
        <Field label="Часовой пояс">
          <Select
            value={form.timezone || "Europe/Moscow"}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          >
            {CLUB_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </Select>
          <p className={md.desktopSettingsGeneralHint} style={{ marginTop: 6 }}>
            Время занятий в расписании групп считается по этому поясу. После
            смены будущие занятия пересоздаются автоматически.
          </p>
        </Field>
        <Field label="О клубе">
          <Textarea
            value={form.portal_description || ""}
            onChange={(e) =>
              setForm({ ...form, portal_description: e.target.value })
            }
            rows={4}
            placeholder="Краткое описание для клиентов: направления, атмосфера, как добраться"
          />
        </Field>
      </div>
    </div>
  );

  const generalFieldsApp = (
    <>
      <Field label="Название клуба">
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </Field>
      <Field label="Телефон">
        <Input
          value={form.phone || ""}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </Field>
      <Field label="Адрес">
        <Input
          value={form.address || ""}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </Field>
      <Field label="Часовой пояс">
        <Select
          value={form.timezone || "Europe/Moscow"}
          onChange={(e) => setForm({ ...form, timezone: e.target.value })}
        >
          {CLUB_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </Select>
        <p className={aa.hubHint} style={{ marginTop: 6 }}>
          Время в расписании групп — по этому поясу. После смены занятия
          пересоздаются.
        </p>
      </Field>
      <Field label="О клубе">
        <Textarea
          value={form.portal_description || ""}
          onChange={(e) =>
            setForm({ ...form, portal_description: e.target.value })
          }
          rows={4}
          placeholder="Краткое описание для клиентов: направления, атмосфера, как добраться"
        />
      </Field>
    </>
  );

  const panelClass = isApp ? s.settingsPanel : md.desktopSettingsSection;
  const trainersScheduleToggle = (
    <label className={s.settingsToggleRow}>
      <input
        type="checkbox"
        checked={!!form.trainers_see_all_schedule}
        onChange={(e) =>
          setForm({
            ...form,
            trainers_see_all_schedule: e.target.checked,
          })
        }
      />
      <span>
        <strong>Тренеры видят занятия всех тренеров</strong>
        <span className={s.branchCheckAddr}>
          В календаре можно будет переключаться между своими и всеми занятиями.
          Журнал, отметки и редактирование останутся доступны только для своих.
        </span>
      </span>
    </label>
  );

  const tabPanels = (
    <>
      {tab === "general" && (
        <div className={panelClass}>
          <form onSubmit={save} className={isApp ? aa.absenceChargeSettingsStack : undefined}>
            {isApp ? (
              <ActTarget stepId="settings" subId="settings_club" as="div" className={aa.absenceChargeSettingsStack}>
              <HubFormCard title="Клуб">{generalFieldsApp}</HubFormCard>
              <HubFormCard title="Расписание тренеров">
                {trainersScheduleToggle}
              </HubFormCard>
              </ActTarget>
            ) : (
              <ActTarget stepId="settings" subId="settings_club" as="div">
              {generalFieldsDesktop}
              <section className={md.desktopSettingsNotifyCard}>
                <h3 className={md.desktopSettingsSubheading}>Расписание тренеров</h3>
                <p className={md.desktopGroupSettingsLead}>
                  Кто что видит в кабинете тренера.
                </p>
                {trainersScheduleToggle}
              </section>
              </ActTarget>
            )}
            {isApp ? saveButton(true) : null}
          </form>
          <AbsenceChargeSettingsPanel variant={isApp ? "app" : "desktop"} />
          {owner ? (
            isApp ? (
              <HubFormCard title="Удаление клуба">
                <p className={aa.hubHint}>
                  Клуб исчезнет из кабинета. Пользователи потеряют доступ.
                  Восстановить нельзя.
                </p>
                {!showDeleteClub ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowDeleteClub(true)}
                  >
                    Удалить клуб…
                  </Button>
                ) : (
                  <div className={aa.absenceChargeSettingsStack}>
                    <Field
                      label={`Введите «${form.name || authTenant?.name || "название"}» для подтверждения`}
                    >
                      <Input
                        value={deleteConfirmName}
                        onChange={(e) => setDeleteConfirmName(e.target.value)}
                        placeholder="Название клуба"
                        autoComplete="off"
                      />
                    </Field>
                    <div className={s.inlineFieldRow}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setShowDeleteClub(false);
                          setDeleteConfirmName("");
                        }}
                      >
                        Отмена
                      </Button>
                      <Button
                        type="button"
                        disabled={
                          deletingClub ||
                          deleteConfirmName.trim() !==
                            String(form.name || authTenant?.name || "").trim()
                        }
                        onClick={deleteCurrentClub}
                      >
                        {deletingClub ? "Удаляем…" : "Удалить навсегда"}
                      </Button>
                    </div>
                  </div>
                )}
              </HubFormCard>
            ) : (
              <section className={md.desktopSettingsDangerCard}>
                <h3 className={md.desktopSettingsSubheading}>Удаление клуба</h3>
                <p className={md.desktopGroupSettingsLead}>
                  Клуб пропадёт из списка, доступ сотрудников и клиентов
                  закроется. Данные останутся для статистики платформы —
                  восстановить клуб нельзя.
                </p>
                {!showDeleteClub ? (
                  <div className={md.desktopSettingsDangerActions}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowDeleteClub(true)}
                    >
                      Удалить клуб…
                    </Button>
                  </div>
                ) : (
                  <div className={md.desktopSettingsDangerActions} style={{ maxWidth: 420, flexDirection: "column" }}>
                    <Field
                      label={`Введите «${form.name || authTenant?.name || "название"}» для подтверждения`}
                    >
                      <Input
                        value={deleteConfirmName}
                        onChange={(e) => setDeleteConfirmName(e.target.value)}
                        placeholder="Название клуба"
                        autoComplete="off"
                      />
                    </Field>
                    <div className={s.inlineFieldRow}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setShowDeleteClub(false);
                          setDeleteConfirmName("");
                        }}
                      >
                        Отмена
                      </Button>
                      <Button
                        type="button"
                        disabled={
                          deletingClub ||
                          deleteConfirmName.trim() !==
                            String(form.name || authTenant?.name || "").trim()
                        }
                        onClick={deleteCurrentClub}
                      >
                        {deletingClub ? "Удаляем…" : "Удалить навсегда"}
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            )
          ) : null}
        </div>
      )}

      {tab === "portal" && (
        <SettingsPortalBrandingPanel
          accentColor={form.portal_accent_color}
          coverUrl={form.portal_cover_url}
          logoUrl={form.logo_url}
          fontFamily={form.portal_font_family}
          themeMode={form.portal_theme_mode}
          galleryUrls={form.portal_gallery}
          fullBranding={plan.fullBranding}
          onUpdated={mergeTenant}
          compact={isApp}
          desktop={!isApp}
          rootClassName={!isApp ? md.desktopSettingsBrandPage : ""}
        />
      )}

      {tab === "payments" && showPaymentsTab && (
        <SettingsAcquiringPanel compact={isApp} desktop={!isApp} />
      )}

      {tab === "notify" && (
        <ActTarget stepId="settings" subId="settings_notify" as="div">
        <form onSubmit={save} className={panelClass}>
          {isApp ? (
            <HubFormCard title="Клиентам">
              <p className={s.onboardingSheetHint}>
                Автоматические напоминания о предстоящих занятиях из расписания
                клуба.
              </p>
              <label className={s.settingsToggleRow}>
                <input
                  type="checkbox"
                  checked={form.patient_reminder_enabled !== false}
                  onChange={(e) =>
                    setForm({ ...form, patient_reminder_enabled: e.target.checked })
                  }
                />
                <span>
                  <strong>Напоминание перед тренировкой</strong>
                  <span className={s.branchCheckAddr}>
                    За 4 часа до начала занятия — push в приложении клиента. Если
                    push не подключён, уйдёт email с датой, временем и тренером.
                  </span>
                </span>
              </label>
            </HubFormCard>
          ) : (
            <section className={md.desktopSettingsNotifyCard}>
              <h3 className={md.desktopSettingsSubheading}>Клиентам</h3>
              <p className={md.desktopGroupSettingsLead}>
                Автоматические напоминания о предстоящих занятиях из расписания
                клуба.
              </p>
              <label className={s.settingsToggleRow}>
                <input
                  type="checkbox"
                  checked={form.patient_reminder_enabled !== false}
                  onChange={(e) =>
                    setForm({ ...form, patient_reminder_enabled: e.target.checked })
                  }
                />
                <span>
                  <strong>Напоминание перед тренировкой</strong>
                  <span className={s.branchCheckAddr}>
                    За 4 часа до начала занятия — push в приложении клиента. Если
                    push не подключён, уйдёт email с датой, временем и тренером.
                  </span>
                </span>
              </label>
            </section>
          )}

          {plan.polls ? (
            <AttendancePollSettingsPanel compact={isApp} />
          ) : null}

          <TrainerNotifySettingsPanel compact={isApp} />

          {isApp ? (
            <HubFormCard title="Мне на этом устройстве">
              <AdminPushBlock />
            </HubFormCard>
          ) : (
            <section className={md.desktopSettingsNotifyCard}>
              <h3 className={md.desktopSettingsSubheading}>Мне на этом устройстве</h3>
              <AdminPushBlock />
            </section>
          )}

          {isApp ? saveButton(true) : null}
        </form>
        </ActTarget>
      )}

      {tab === "data" && (
        <div className={panelClass}>
          {isApp ? (
            <div className={aa.hubListGroup}>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={() => setImportOpen(true)}
              >
                <span>
                  <strong>Импорт клиентов</strong>
                  <span className={aa.hubHint}>CSV: ФИО, email, телефон…</span>
                </span>
              </button>
              <button
                type="button"
                className={aa.hubLinkRow}
                onClick={async () => {
                  try {
                    const res = await clinicAPI.exportPatientsCsv();
                    downloadBlobFile(res.data, "Клиенты.csv");
                    toast.success("CSV скачан");
                  } catch (e) {
                    toast.error(e.response?.data?.error || "Ошибка");
                  }
                }}
              >
                <span>
                  <strong>Экспорт клиентов</strong>
                  <span className={aa.hubHint}>Скачать реестр CSV</span>
                </span>
              </button>
              <Link to="/manage/app/clients" className={aa.hubLinkRow}>
                <span>
                  <strong>Реестр клиентов</strong>
                  <span className={aa.hubHint}>Поиск и карточки</span>
                </span>
              </Link>
              {helpEnabled ? (
              <Link to="/manage/app/help" className={aa.hubLinkRow}>
                <span>
                  <strong>База знаний</strong>
                  <span className={aa.hubHint}>
                    Инструкции и полное обучение по разделам
                  </span>
                </span>
              </Link>
              ) : null}
              {onboardingEnabled ? (
                <button
                  type="button"
                  className={aa.hubLinkRow}
                  onClick={restoreOnboarding}
                >
                  <span>
                    <strong>Мастер настройки клуба</strong>
                    <span className={aa.hubHint}>
                      Вернуть шаги онбординга, если закрыли их
                    </span>
                  </span>
                </button>
              ) : null}
              {quickStartEnabled ? (
                <button
                  type="button"
                  className={aa.hubLinkRow}
                  onClick={restartQuickSetup}
                >
                  <span>
                    <strong>Быстрый старт</strong>
                        <span className={aa.hubHint}>
                      6 шагов на живых экранах: группа → журнал
                    </span>
                  </span>
                </button>
              ) : null}
              {quickStartEnabled && (trainingActive || activationVisible) ? (
                <button
                  type="button"
                  className={aa.hubLinkRow}
                  onClick={exitAnyGuide}
                >
                  <span>
                    <strong>Скрыть подсказки</strong>
                    <span className={aa.hubHint}>
                      Свернуть панель обучения или настройки
                    </span>
                  </span>
                </button>
              ) : null}
            </div>
          ) : (
            <div className={md.desktopSettingsDataGrid}>
              <div className={s.settingsDataCard}>
                <strong>Импорт клиентов</strong>
                <p className={s.docSectionHint}>
                  Загрузка CSV с колонками ФИО, email, телефон и др.
                </p>
                <Button type="button" onClick={() => setImportOpen(true)}>
                  Импорт CSV
                </Button>
              </div>
              <div className={s.settingsDataCard}>
                <strong>Экспорт клиентов</strong>
                <p className={s.docSectionHint}>
                  Скачать реестр для редактирования и повторного импорта
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={async () => {
                    try {
                      const res = await clinicAPI.exportPatientsCsv();
                      downloadBlobFile(res.data, "Клиенты.csv");
                      toast.success("CSV скачан");
                    } catch (e) {
                      toast.error(e.response?.data?.error || "Ошибка");
                    }
                  }}
                >
                  Скачать CSV
                </Button>
              </div>
              <Link to="/manage/app/clients" className={s.settingsDataCard}>
                <strong>Реестр клиентов</strong>
                <span className={s.docSectionHint}>Поиск, дубликаты, карточки</span>
              </Link>
              {helpEnabled ? (
              <Link to="/manage/app/help" className={s.settingsDataCard}>
                <strong>База знаний</strong>
                <span className={s.docSectionHint}>
                  Инструкции и полное обучение по разделам
                </span>
              </Link>
              ) : null}
              {onboardingEnabled ? (
                <div className={s.settingsDataCard}>
                  <strong>Мастер настройки клуба</strong>
                  <p className={s.docSectionHint}>
                    Вернуть шаги онбординга на главный экран, если закрыли их
                    кнопкой «Позже»
                  </p>
                  <Button type="button" variant="secondary" onClick={restoreOnboarding}>
                    Показать онбординг
                  </Button>
                </div>
              ) : null}
              {quickStartEnabled ? (
                <div className={s.settingsDataCard}>
                  <strong>Быстрый старт</strong>
                  <p className={s.docSectionHint}>
                    Подсказки на живых экранах: 6 шагов до журнала. Зал и
                    направление создаются автоматически.
                  </p>
                  <Button type="button" variant="secondary" onClick={restartQuickSetup}>
                    Пройти снова
                  </Button>
                </div>
              ) : null}
              {quickStartEnabled && (trainingActive || activationVisible) ? (
                <div className={s.settingsDataCard}>
                  <strong>Подсказки на экране</strong>
                  <p className={s.docSectionHint}>
                    Свернуть панель обучения или быстрого старта
                  </p>
                  <Button type="button" variant="secondary" onClick={exitAnyGuide}>
                    Скрыть подсказки
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (tabFromUrl === "registration") {
    return <Navigate to="/manage/app/clients/registration" replace />;
  }

  if (loading) {
    const skeleton = <SkeletonCard />;
    return isApp ? (
      <HubScreen title="Настройки">
        {skeleton}
      </HubScreen>
    ) : (
      <div className={md.desktopSettingsPage}>{skeleton}</div>
    );
  }

  if (isApp) {
    const roleLabel =
      normalizeRole(role) === "owner"
        ? "Владелец"
        : normalizeRole(role) === "admin"
          ? "Администратор"
          : "Сотрудник";

    return (
      <HubScreen title="Настройки">
        <ActivationPageGuide stepIds={["settings"]} />
        {hasAdminAccess(role) || owner ? (
          <button
            type="button"
            className={aa.moreProfile}
            onClick={() => navigate("/manage/app/profile")}
          >
            <Avatar
              name={profile?.full_name || "?"}
              src={mediaUrl(profile?.photo_url)}
              size="md"
              className={aa.moreAvatarImg}
            />
            <div className={aa.moreProfileInfo}>
              <p className={aa.moreProfileName}>{profile?.full_name || "—"}</p>
              <p className={aa.moreProfileRole}>
                {roleLabel} · Профиль
              </p>
            </div>
            <span className={aa.moreProfileChevron} aria-hidden />
          </button>
        ) : null}

        <div className={aa.settingsHubClubRow}>
          {form.logo_url ? (
            <img src={form.logo_url} alt="" className={aa.settingsHubClubLogo} />
          ) : (
            <div className={aa.settingsHubClubLogoPh}>
              {(form.name || "К").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className={aa.settingsHubClubMeta}>
            <strong>{form.name || "Клуб"}</strong>
            <span>
              {form.subdomain ? `${form.subdomain} · ` : ""}
              {STATUS_LABELS[form.status] || form.status || "—"}
            </span>
          </div>
        </div>

        <ActTarget stepId="settings" subId="settings_tabs" as="div">
        <HubSegment tabs={visibleTabs} active={tab} onChange={setTabAndUrl} />
        </ActTarget>
        {tabPanels}

        <AdminPatientImport
          open={importOpen}
          onClose={() => setImportOpen(false)}
          appMode={isApp}
        />
      </HubScreen>
    );
  }

  return (
    <div className={md.desktopSettingsPage}>
      <ActivationPageGuide stepIds={["settings"]} />
      <div className={md.desktopSettingsPanel}>
        <header className={md.desktopSettingsHead}>
          <ActTarget stepId="settings" subId="settings_club" as="div">
          <div className={md.desktopSettingsClub}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" className={md.desktopSettingsClubLogo} />
            ) : (
              <div className={md.desktopSettingsClubLogoPh}>
                {(form.name || "К").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className={md.desktopSettingsClubText}>
              <p className={md.desktopSettingsClubName}>{form.name || "Клуб"}</p>
              <div className={md.desktopSettingsClubMetaRow}>
                {form.subdomain ? (
                  <span className={md.desktopSettingsChip}>{form.subdomain}</span>
                ) : null}
                <span
                  className={[
                    md.desktopSettingsStatus,
                    form.status === "active"
                      ? md.desktopSettingsStatusActive
                      : form.status === "suspended"
                        ? md.desktopSettingsStatusSuspended
                        : md.desktopSettingsStatusTrial,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {STATUS_LABELS[form.status] || form.status || "—"}
                </span>
                {form.phone ? (
                  <span className={md.desktopSettingsMetaMuted}>{form.phone}</span>
                ) : null}
              </div>
            </div>
          </div>
          </ActTarget>
          <div className={md.desktopSettingsHeadActions}>
            <Button onClick={save} disabled={saving}>
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
          </div>
        </header>

        <div className={md.desktopSettingsTabs}>
          <ActTarget stepId="settings" subId="settings_tabs" as="div">
          <Tabs items={visibleTabs} active={tab} onChange={setTabAndUrl} />
          </ActTarget>
        </div>

        <div className={md.desktopSettingsBody}>{tabPanels}</div>
      </div>

      <AdminPatientImport open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}
